import type { ComponentType } from 'react';
import {
  Button,
  IconButton,
  LabelDefault,
  Menu,
  Tooltip,
} from '@terra/ui-design-system';
import { Box, HStack } from '@terra/ui-styled-system';
import { ArrowDown01Icon } from '@terra/ui-icons';
import { TARGET_SELECTOR_LABEL_SEPARATOR } from './formatTargetSelectorLabel';

const triggerFillStyles = {
  bg: 'bg.neutral.subtle',
} as const;

const triggerInteractionStyles = {
  bg: 'transparent',
  _hover: triggerFillStyles,
  _open: {
    bg: 'bg.neutral.subtle',
    _hover: triggerFillStyles,
  },
} as const;

const tooltipPositioning = {
  placement: 'right',
  strategy: 'fixed',
} as const;

type TargetSelectorTriggerProps = {
  collapsed: boolean;
  ariaLabel: string;
  tooltipTitle: string;
  icon: ComponentType<{
    size?: number;
  }>;
  primaryLabel: string;
  secondaryLabel?: string;
};

export function TargetSelectorTrigger({
  collapsed,
  ariaLabel,
  tooltipTitle,
  icon: Icon,
  primaryLabel,
  secondaryLabel,
}: TargetSelectorTriggerProps) {
  if (collapsed) {
    return (
      <Menu.Trigger asChild>
        <IconButton
          variant="ghost"
          size="sm"
          aria-label={ariaLabel}
          cursor="pointer"
          {...triggerInteractionStyles}
        >
          <Tooltip title={tooltipTitle} positioning={tooltipPositioning}>
            <Box display="inline-flex">
              <Icon size={16} />
            </Box>
          </Tooltip>
        </IconButton>
      </Menu.Trigger>
    );
  }

  return (
    <Menu.Trigger asChild>
      <Button
        variant="ghost"
        size="sm"
        w="full"
        h="9"
        justifyContent="space-between"
        px="3.5"
        gap="1"
        aria-label={ariaLabel}
        {...triggerInteractionStyles}
      >
        <Tooltip title={tooltipTitle} positioning={tooltipPositioning}>
          <HStack gap="1" minW="0" alignItems="center" flex="1">
            <Box flexShrink={0} display="flex" alignItems="center">
              <Icon size={16} />
            </Box>
            <Box minW="0" flex="1" truncate data-bi-unmask="true">
              <LabelDefault as="span" color="fg.neutral.default">
                {primaryLabel}
              </LabelDefault>
              {secondaryLabel ? (
                <LabelDefault as="span" color="fg.neutral.muted">
                  {`${TARGET_SELECTOR_LABEL_SEPARATOR}${secondaryLabel}`}
                </LabelDefault>
              ) : null}
            </Box>
          </HStack>
        </Tooltip>
        <ArrowDown01Icon size={16} />
      </Button>
    </Menu.Trigger>
  );
}
